import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { useScopeStore } from '@/stores/scope';

/** `/settings/approvals` (承認ワークフロータブ、v0.2.5 S-11、T-1104)
 *  設計: docs/design/v025-store-node-hierarchy.md「コンフィグ画面」節、
 *  docs/design/v025-store-node-hierarchy-detail.md §承認者解決フロー。
 *
 *  2026-08-03 決定: マスタCSV取込コミットへの承認WF適用は撤回済み (imports.ts の
 *  gate を削除、常時即時コミット)。本画面 (workflow_type='import_commit' の
 *  ルールCRUD) は現時点でどの操作からも参照されないが、承認WFエンジン自体は
 *  将来のシフト管理・労務管理・各種申請書機能で利用予定のため画面ごと残している。
 *  カスタムロール実装後は表示対象を制御できる想定 (CLAUDE.md §6d)。
 *
 *  設計からの意図的な乖離: apps/api/src/lib/approval-rule-engine.ts の rule_json DSL は
 *  and/or/not を無限にネストできる再帰構造だが、フル機能の再帰ツリーGUIビルダーは実装コストが高いため、
 *  本 MVP では「1段のAND/OR + リーフ条件の平坦なリスト」のみを GUI で組み立てられるようにした
 *  (CLAUDE.md §6d 過剰設計回避。ネスト条件が必要な場合は将来 v0.3 でツリーUIに拡張)。 */

type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string; status: string };
type EmployeeCategory = { category_id: string; code: string; label: string };
type EmploymentType = { type_id: string; code: string; label: string };

type ConditionLeaf =
  | { kind: 'employee_attr'; field: 'employee_category_id' | 'employment_type_id' | 'status'; value: string }
  | { kind: 'node_relation'; relation: 'assigned_to_ancestor_of' | 'assigned_to_same_node' };

type RuleForm = {
  rule_id?: string;
  node_id: string; // '' = テナント全体
  operator: 'and' | 'or';
  conditions: ConditionLeaf[];
  explicit_employee_ids: string[];
  priority: number;
};

const emptyForm = (): RuleForm => ({ node_id: '', operator: 'and', conditions: [], explicit_employee_ids: [], priority: 0 });

/** 「条件なし」(右の追加承認者欄のみで運用したい場合) に送信する番兵条件。
 *  CRITICAL bug fix (2026-08-03 QAレビュー): 以前は `{ employee_attr: {} }` を送信しており、
 *  バックエンドの evaluateRuleExpr が `Object.entries({}).every(...)` の vacuous true により
 *  「常にtrue」と評価してしまい、在職中の全社員が承認者になっていた。
 *  評価対象の社員は必ず status='active' に絞り込まれた上で判定される (apps/api/src/lib/
 *  approval-rule-engine.ts の matchEmployeesForExpr 参照) ため、'active' 以外の値との eq 比較は
 *  絶対に一致しない = 常にfalseの番兵条件として機能する。バックエンド側 (ruleExprSchema) も
 *  空の employee_attr を拒否するようになっており、二重に防御している。 */
const NO_CONDITION_RULE_JSON: Record<string, unknown> = { employee_attr: { status: { eq: '__none__' } } };

const buildRuleJson = (form: RuleForm): Record<string, unknown> => {
  if (form.conditions.length === 0) return NO_CONDITION_RULE_JSON;
  const leaves = form.conditions.map((c) => (
    c.kind === 'employee_attr'
      ? { employee_attr: { [c.field]: { eq: c.value } } }
      : { node_relation: { [c.relation]: true } }
  ));
  if (leaves.length === 1) return leaves[0]!;
  return { [form.operator]: leaves };
};

/** 保存済みルールの rule_json (再帰構造) を、平坦な GUI 条件リストに変換できる場合のみ変換する。
 *  ネストした条件式 (and/or の入れ子) の場合は編集不可 (削除のみ) とし、フォームは空で初期化する。
 *  NO_CONDITION_RULE_JSON (番兵条件) は「条件なし」を表すため、空の条件リストとして復元する。 */
const parseRuleJsonToConditions = (json: unknown): { operator: 'and' | 'or'; conditions: ConditionLeaf[] } | null => {
  if (JSON.stringify(json) === JSON.stringify(NO_CONDITION_RULE_JSON)) return { operator: 'and', conditions: [] };
  const asLeaf = (v: unknown): ConditionLeaf | null => {
    if (typeof v !== 'object' || v === null) return null;
    const obj = v as Record<string, unknown>;
    if ('employee_attr' in obj && typeof obj.employee_attr === 'object' && obj.employee_attr !== null) {
      const attrs = obj.employee_attr as Record<string, unknown>;
      const [field, cond] = Object.entries(attrs)[0] ?? [];
      if (!field || typeof cond !== 'object' || cond === null || !('eq' in (cond as Record<string, unknown>))) return null;
      if (field !== 'employee_category_id' && field !== 'employment_type_id' && field !== 'status') return null;
      return { kind: 'employee_attr', field, value: String((cond as Record<string, unknown>).eq) };
    }
    if ('node_relation' in obj && typeof obj.node_relation === 'object' && obj.node_relation !== null) {
      const rel = obj.node_relation as Record<string, unknown>;
      if ('assigned_to_ancestor_of' in rel) return { kind: 'node_relation', relation: 'assigned_to_ancestor_of' };
      if ('assigned_to_same_node' in rel) return { kind: 'node_relation', relation: 'assigned_to_same_node' };
      return null; // assigned_to_specific_node_type はGUI未対応 (編集不可)
    }
    return null;
  };
  if (typeof json !== 'object' || json === null) return null;
  const obj = json as Record<string, unknown>;
  if ('and' in obj || 'or' in obj) {
    const operator: 'and' | 'or' = 'and' in obj ? 'and' : 'or';
    const arr = obj[operator];
    if (!Array.isArray(arr)) return null;
    const leaves = arr.map(asLeaf);
    if (leaves.some((l) => l === null)) return null;
    return { operator, conditions: leaves as ConditionLeaf[] };
  }
  const single = asLeaf(json);
  return single ? { operator: 'and', conditions: [single] } : null;
};

export function ApprovalRuleBuilderPage() {
  const qc = useQueryClient();
  const roots = useScopeStore((s) => s.roots);

  const { data: rules = [], isLoading } = useQuery<Array<{
    rule_id: string; node_id: string | null; rule_json: unknown; explicit_employee_ids: string[]; priority: number;
  }>>({
    queryKey: ['approval-rules'],
    queryFn: () => apiFetch('/approvals/rules'),
  });
  const { data: employees = [] } = useQuery<Employee[]>({ queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees') });
  const { data: categories = [] } = useQuery<EmployeeCategory[]>({ queryKey: ['employee-categories'], queryFn: () => apiFetch<EmployeeCategory[]>('/employee-categories') });
  const { data: employmentTypes = [] } = useQuery<EmploymentType[]>({ queryKey: ['employment-types'], queryFn: () => apiFetch<EmploymentType[]>('/employment-types') });

  const flatNodes = useMemo(() => {
    const result: Array<{ node_id: string; path: string }> = [];
    const walk = (nodes: typeof roots, prefix: string) => {
      for (const n of nodes) {
        const path = prefix ? `${prefix} / ${n.name}` : n.name;
        result.push({ node_id: n.node_id, path });
        walk(n.children, path);
      }
    };
    walk(roots, '');
    return result;
  }, [roots]);

  const employeeName = (id: string) => {
    const e = employees.find((x) => x.employee_id === id);
    return e ? `${e.family_name} ${e.given_name} (${e.employee_number})` : id;
  };

  const [form, setForm] = useState<RuleForm | null>(null);
  const [deleting, setDeleting] = useState<{ rule_id: string } | null>(null);

  const previewMut = useMutation({
    mutationFn: (body: { rule_json: Record<string, unknown>; explicit_employee_ids: string[]; target_node_id: string | null }) =>
      apiFetch<{ rule_valid: boolean; approvers: Array<{ employee_id: string; name: string }> }>('/approvals/preview-rule', {
        method: 'POST', body: JSON.stringify(body),
      }),
  });

  useEffect(() => {
    if (!form) { previewMut.reset(); return; }
    // 条件も追加承認者も未設定の真っ新なフォームでは「0名」プレビューを出しても意味がないため呼ばない
    if (form.conditions.length === 0 && form.explicit_employee_ids.length === 0) { previewMut.reset(); return; }
    const ruleJson = buildRuleJson(form);
    const timer = setTimeout(() => {
      previewMut.mutate({
        rule_json: ruleJson,
        explicit_employee_ids: form.explicit_employee_ids,
        target_node_id: form.node_id || null,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.operator, form?.conditions, form?.explicit_employee_ids, form?.node_id]);

  const saveMut = useMutation({
    mutationFn: (v: RuleForm) => {
      const ruleJson = buildRuleJson(v);
      const body = { node_id: v.node_id || null, rule_json: ruleJson, explicit_employee_ids: v.explicit_employee_ids, priority: v.priority };
      return v.rule_id
        ? apiFetch(`/approvals/rules/${v.rule_id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : apiFetch('/approvals/rules', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-rules'] });
      setForm(null);
      toast.success('承認ルールを保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (ruleId: string) => apiFetch(`/approvals/rules/${ruleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-rules'] });
      setDeleting(null);
      toast.success('承認ルールを削除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const addCondition = () => {
    if (!form) return;
    setForm({ ...form, conditions: [...form.conditions, { kind: 'employee_attr', field: 'status', value: 'active' }] });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">承認ワークフロー</h2>
          <p className="text-sm text-neutral-sub mt-1">
            承認者を決めるルールを設定します。現在この承認ワークフローに紐づく機能はありません
            (今後追加予定のシフト申請等の機能で利用されます)。
          </p>
        </div>
        <button type="button" onClick={() => setForm(emptyForm())}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap">
          <Plus size={16} /> ルール追加
        </button>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="text-left px-4 py-2">適用範囲</th>
                <th className="text-left px-4 py-2">条件</th>
                <th className="text-left px-4 py-2">追加承認者</th>
                <th className="text-right px-4 py-2 w-16">優先度</th>
                <th className="w-20 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const path = r.node_id ? (flatNodes.find((n) => n.node_id === r.node_id)?.path ?? r.node_id) : 'テナント全体';
                return (
                  <tr key={r.rule_id} className="border-t border-neutral-line">
                    <td className="px-4 py-2">{path}</td>
                    <td className="px-4 py-2 font-mono text-xs text-neutral-sub max-w-xs truncate">{JSON.stringify(r.rule_json)}</td>
                    <td className="px-4 py-2 text-xs text-neutral-sub max-w-[160px] truncate" title={r.explicit_employee_ids.map(employeeName).join('、')}>
                      {r.explicit_employee_ids.length > 0 ? r.explicit_employee_ids.map(employeeName).join('、') : '−'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.priority}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button type="button" aria-label="編集"
                        onClick={() => {
                          const parsed = parseRuleJsonToConditions(r.rule_json);
                          setForm({
                            rule_id: r.rule_id, node_id: r.node_id ?? '', priority: r.priority,
                            explicit_employee_ids: r.explicit_employee_ids,
                            operator: parsed?.operator ?? 'and', conditions: parsed?.conditions ?? [],
                          });
                        }}
                        className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleting({ rule_id: r.rule_id })}
                        className="ml-1 p-1 text-neutral-sub hover:text-warning-text"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rules.length === 0 && (
            <EmptyState message="承認ルールが登録されていません" description="「ルール追加」から承認者の条件を設定してください" />
          )}
        </div>
      )}

      {/* Design MAJOR対応 (2026-08-03レビュー): 自作の fixed/overlay div ではなく共有Modal
          (role="dialog"/aria-modal/Escape/フォーカストラップ完備) を使う */}
      <Modal
        open={form !== null}
        title={form?.rule_id ? 'ルール編集' : 'ルール追加'}
        onClose={() => setForm(null)}
        size="lg"
        footer={form && (
          <>
            <button type="button" onClick={() => setForm(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="button" disabled={saveMut.isPending} onClick={() => saveMut.mutate(form)}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {saveMut.isPending ? '保存中…' : '保存'}
            </button>
          </>
        )}
      >
        {form && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* 左: 条件ビルダー */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-neutral-sub mb-1">適用範囲</label>
                  <select value={form.node_id} onChange={(e) => setForm({ ...form, node_id: e.target.value })}
                    className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                    <option value="">テナント全体</option>
                    {flatNodes.map((n) => <option key={n.node_id} value={n.node_id}>{n.path}</option>)}
                  </select>
                </div>

                <div className="border border-neutral-line rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-sub">条件式 (承認者になれる社員の条件)</span>
                    <button type="button" onClick={addCondition} className="text-xs text-brand-red hover:text-brand-red-hover flex items-center gap-0.5">
                      <Plus size={12} /> 条件追加
                    </button>
                  </div>
                  {form.conditions.length > 1 && (
                    <div className="flex items-center gap-2 text-xs text-neutral-sub">
                      条件の結合:
                      <select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value as 'and' | 'or' })}
                        className="px-2 py-1 border border-neutral-line rounded">
                        <option value="and">すべて満たす (AND)</option>
                        <option value="or">いずれか満たす (OR)</option>
                      </select>
                    </div>
                  )}
                  {form.conditions.length === 0 && (
                    <p className="text-xs text-neutral-sub">条件なし。右の「追加承認者」欄のみで承認者を指定する運用も可能です。</p>
                  )}
                  {form.conditions.map((cond, idx) => (
                    <div key={idx} className="flex items-center gap-2 flex-wrap">
                      <select value={cond.kind === 'employee_attr' ? cond.field : `relation:${cond.relation}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          const next = [...form.conditions];
                          if (v.startsWith('relation:')) {
                            next[idx] = { kind: 'node_relation', relation: v.slice('relation:'.length) as 'assigned_to_ancestor_of' | 'assigned_to_same_node' };
                          } else {
                            next[idx] = { kind: 'employee_attr', field: v as 'employee_category_id' | 'employment_type_id' | 'status', value: '' };
                          }
                          setForm({ ...form, conditions: next });
                        }}
                        className="px-2 py-1 border border-neutral-line rounded text-sm">
                        <option value="status">社員ステータス</option>
                        <option value="employee_category_id">社員区分</option>
                        <option value="employment_type_id">雇用形態</option>
                        <option value="relation:assigned_to_ancestor_of">対象ノードの上位に配属</option>
                        <option value="relation:assigned_to_same_node">対象ノードと同一に配属</option>
                      </select>
                      {cond.kind === 'employee_attr' && cond.field === 'status' && (
                        <select value={cond.value} onChange={(e) => {
                          const next = [...form.conditions]; next[idx] = { ...cond, value: e.target.value }; setForm({ ...form, conditions: next });
                        }} className="px-2 py-1 border border-neutral-line rounded text-sm">
                          <option value="active">在職中</option>
                        </select>
                      )}
                      {cond.kind === 'employee_attr' && cond.field === 'employee_category_id' && (
                        <select value={cond.value} onChange={(e) => {
                          const next = [...form.conditions]; next[idx] = { ...cond, value: e.target.value }; setForm({ ...form, conditions: next });
                        }} className="px-2 py-1 border border-neutral-line rounded text-sm">
                          <option value="">選択してください</option>
                          {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.label}</option>)}
                        </select>
                      )}
                      {cond.kind === 'employee_attr' && cond.field === 'employment_type_id' && (
                        <select value={cond.value} onChange={(e) => {
                          const next = [...form.conditions]; next[idx] = { ...cond, value: e.target.value }; setForm({ ...form, conditions: next });
                        }} className="px-2 py-1 border border-neutral-line rounded text-sm">
                          <option value="">選択してください</option>
                          {employmentTypes.map((t) => <option key={t.type_id} value={t.type_id}>{t.label}</option>)}
                        </select>
                      )}
                      <button type="button" aria-label="条件を削除"
                        onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, i) => i !== idx) })}
                        className="p-1 text-neutral-sub hover:text-warning-text"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>

                <div className="border border-neutral-line rounded p-3 space-y-2 max-h-48 overflow-y-auto">
                  <span className="text-sm text-neutral-sub">追加承認者 (条件に関わらず常に承認可能にする社員)</span>
                  {employees.length === 0 && <p className="text-xs text-neutral-sub">社員が登録されていません</p>}
                  {employees.map((e) => (
                    <label key={e.employee_id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.explicit_employee_ids.includes(e.employee_id)}
                        onChange={(ev) => {
                          const next = ev.target.checked
                            ? [...form.explicit_employee_ids, e.employee_id]
                            : form.explicit_employee_ids.filter((id) => id !== e.employee_id);
                          setForm({ ...form, explicit_employee_ids: next });
                        }} />
                      {e.family_name} {e.given_name} ({e.employee_number})
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-sm text-neutral-sub mb-1">優先度 (数値が大きいほど優先)</label>
                  <input type="number" min={0} max={9999} value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                    className="w-32 px-3 py-2 rounded border border-neutral-line focus-ring" />
                </div>
              </div>

              {/* 右: プレビュー */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-neutral-text">プレビュー: 実際に承認者になる社員</h4>
                <div className="border border-neutral-line rounded p-3 min-h-[160px] bg-neutral-panel/40">
                  {previewMut.isPending && <p className="text-sm text-neutral-sub">計算中…</p>}
                  {!previewMut.isPending && previewMut.data && previewMut.data.rule_valid === false && (
                    <div className="flex items-start gap-2 p-3 rounded bg-warning-bg border border-warning-border">
                      <AlertTriangle size={16} className="text-warning-text flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-sm text-warning-text">条件式が不正です。追加承認者のみが承認可能になります。</p>
                    </div>
                  )}
                  {/* Design MAJOR対応 (2026-08-03レビュー): NodesPageの依存件数警告と同じ
                      AlertTriangle + bg-warning-bg + border-warning-border パターンに揃える */}
                  {!previewMut.isPending && previewMut.data && previewMut.data.rule_valid !== false && previewMut.data.approvers.length === 0 && (
                    <div className="flex items-start gap-2 p-3 rounded bg-warning-bg border border-warning-border">
                      <AlertTriangle size={16} className="text-warning-text flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-sm text-warning-text">この条件では承認者が0名になります。</p>
                    </div>
                  )}
                  {!previewMut.isPending && previewMut.data && previewMut.data.approvers.length > 0 && (
                    <ul className="text-sm space-y-1">
                      {previewMut.data.approvers.map((a) => (
                        <li key={a.employee_id} className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-red" /> {a.name}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!previewMut.isPending && !previewMut.data && (
                    <p className="text-sm text-neutral-sub">条件を追加するとプレビューが表示されます</p>
                  )}
                </div>
              </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="承認ルールを削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.rule_id); }}
        message={<p className="text-sm">このルールを削除すると、対象範囲での承認者が変わります。</p>}
      />
    </div>
  );
}
